/**
 * Feed reset cadence.
 *
 * The team picks a monthly reset rule ("last day of the month", "the 15th",
 * …). It defines a recurring CYCLE WINDOW for the closet wall: on the reset
 * day the wall resets (all closet doors shut), and through the new cycle each
 * released post pops its closet door open.
 *
 * Crucially this needs NO cron: "fresh this cycle" = published_at >=
 * currentCycleStart(rule). The instant the reset day passes, currentCycleStart
 * rolls forward and last cycle's posts stop counting as fresh, so the doors
 * shut on the next load automatically - same trick as future-dated publishing.
 *
 * Stored in public.app_settings key='feed_cycle', served by
 * /api/settings/feed-cycle (GET public, PUT admin).
 */

export type CadenceType = 'last_day' | 'day_of_month';

/** null = no cadence configured (feature off; doors behave normally). */
export type FeedCycleRule = { type: CadenceType; day: number | null } | null;

interface RawValue {
  type: CadenceType | null;
  day: number | null;
}

function toRule(v: RawValue | null | undefined): FeedCycleRule {
  if (!v || (v.type !== 'last_day' && v.type !== 'day_of_month')) return null;
  return { type: v.type, day: typeof v.day === 'number' ? v.day : null };
}

/** Fetch the authoritative cadence. Never throws - falls back to off (null). */
export async function fetchFeedCycle(signal?: AbortSignal): Promise<FeedCycleRule> {
  try {
    const res = await fetch('/api/settings/feed-cycle', { signal });
    if (!res.ok) return null;
    return toRule((await res.json()) as RawValue);
  } catch {
    return null;
  }
}

/** Admin save. Returns the normalised stored rule. */
export async function saveFeedCycle(rule: FeedCycleRule, accessToken: string): Promise<FeedCycleRule> {
  const body = rule ? { type: rule.type, day: rule.day } : { type: null };
  const res = await fetch('/api/settings/feed-cycle', {
    method: 'PUT',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? `Save failed (${res.status}).`;
    const detail = (data as any)?.detail;
    throw new Error(detail ? `${err}: ${detail}` : err);
  }
  return toRule(data as RawValue);
}

// ---------- Cycle math (local time) ---------------------------------------

/** The reset instant (00:00 local on the reset day) within a given month. */
function resetDateInMonth(rule: NonNullable<FeedCycleRule>, year: number, monthIndex: number): Date {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const day = rule.type === 'last_day' ? lastDay : Math.min(rule.day ?? lastDay, lastDay);
  return new Date(year, monthIndex, day, 0, 0, 0, 0);
}

/** The reset day-of-month for a given month (clamped), or null when off. */
export function resetDayInMonth(rule: FeedCycleRule, year: number, monthIndex: number): number | null {
  if (!rule) return null;
  return resetDateInMonth(rule, year, monthIndex).getDate();
}

/** Start of the current cycle (most recent reset at/just before `now`). */
export function currentCycleStart(rule: FeedCycleRule, now: Date = new Date()): Date | null {
  if (!rule) return null;
  const thisMonth = resetDateInMonth(rule, now.getFullYear(), now.getMonth());
  if (now.getTime() >= thisMonth.getTime()) return thisMonth;
  return resetDateInMonth(rule, now.getFullYear(), now.getMonth() - 1);
}

/** The next reset instant strictly after `now`. */
export function nextReset(rule: FeedCycleRule, now: Date = new Date()): Date | null {
  if (!rule) return null;
  const thisMonth = resetDateInMonth(rule, now.getFullYear(), now.getMonth());
  if (now.getTime() < thisMonth.getTime()) return thisMonth;
  return resetDateInMonth(rule, now.getFullYear(), now.getMonth() + 1);
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Human label, e.g. "the last day of each month" / "the 15th of each month". */
export function describeRule(rule: FeedCycleRule): string {
  if (!rule) return 'No reset: content stays on the wall until replaced.';
  if (rule.type === 'last_day') return 'the last day of each month';
  return `the ${ordinal(rule.day ?? 1)} of each month`;
}
